/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import {
    Heading,
    Button,
    Text,
    Stack,
    Icon,
    Select,
    SelectOption,
    Radio,
    Box,
    FlexRow,
    FlexCol,
    RadioGroupWithValidation,
} from 'sarsaparilla';

import PassImages from './PassImages';
import {
    changePassesDetails,
    getDraftPriceSummary,
} from '../../actions/orderDetailsActions';
import {
    isGoldenSpike,
    isRedRock,
    isCochiti,
    isYosemite,
    isDenali,
    isBrainardLake,
    isMountEvans,
} from '../../constants';

const passOptions = [
    {
        title: 'Annual',
        value: 'INTERAGENCY_ANNUAL_PASS',
    },
    {
        title: 'Annual Senior',
        value: 'INTERAGENCY_ANNUAL_SENIOR_PASS',
    },
    {
        title: 'Lifetime Senior',
        value: 'INTERAGENCY_LIFETIME_SENIOR_PASS',
    },
    {
        title: 'Access',
        value: 'INTERAGENCY_ACCESS_PASS',
    },
    {
        title: 'Volunteer',
        value: 'INTERAGENCY_VOLUNTEER_PASS',
    },
    {
        title: 'Military',
        value: 'INTERAGENCY_MILITARY_PASS',
    },
    {
        title: 'Military Lifetime',
        value: 'INTERAGENCY_LIFETIME_MILITARY_PASS',
    },
    {
        title: '4th Grade',
        value: 'INTERAGENCY_FOURTH_GRADE_PASS',
    },
    {
        title: 'Resident',
        value: 'INTERAGENCY_RESIDENT_PASS',
    },
    {
        title: 'Non-Resident',
        value: 'INTERAGENCY_NONRESIDENT_PASS',
    },
    {
        title: 'Golden Age',
        value: 'GOLDEN_AGE_PASS',
    },
    {
        title: 'Golden Access',
        value: 'GOLDEN_ACCESS_PASS',
    },
];

function PassPicker({
    value,
    changeValue,
    index,
    quantity,
    deletePassPicker,
    addValidationRef,
    facilityId,
}) {
    const { radioValue, selectValue } = value;
    const id = `pass-picker-select-${index}`;

    const getRadioGroup = () => {
        if (
            isGoldenSpike(facilityId) ||
            isRedRock(facilityId) ||
            isYosemite(facilityId) ||
            isDenali(facilityId)
        ) {
            return (
                <>
                    <RadioGroupWithValidation
                        ref={addValidationRef(10 + index * 2)}
                        label="Pass"
                        id={`pass-radios-${id}`}
                        name={`pass-radios-${id}`}
                        value={radioValue}
                        onChange={(e) =>
                            changeValue({ index, value: { radioValue: e.target.value } })
                        }
                        isRequired
                    >
                        <Radio
                            id={`noPass-${id}`}
                            value="noPass"
                            label="I will purchase a pass that covers the entrance/standard amenity fee when I arrive onsite"
                            className="mb-1"
                        />
                        <Radio
                            id={`sitePass-${id}`}
                            value="sitePass"
                            label="I will present a valid site pass for the location I am planning to visit"
                            className="mb-1"
                        />
                        <Radio
                            id={`has5thGPass-${id}`}
                            value="has5thGPass"
                            label="I will present a valid U.S. 5th Grade Voucher--valid through August 31, 2021"
                            className="mb-1"
                        />
                        <Radio
                            id={`hasUSACVPass-${id}`}
                            value="hasUSACVPass"
                            label="I will present ID proof of my U.S. Armed Service Veteran or Gold Star Dependent status upon arrival"
                            className="mb-1"
                        />
                        <Radio
                            id={`hasPass-${id}`}
                            value="hasPass"
                            label="I will present a valid Interagency Annual/Lifetime Pass"
                            className="mb-1"
                        />
                    </RadioGroupWithValidation>
                    {radioValue === 'hasPass' && (
                        <FlexRow>
                            <FlexCol md={7} lg={8}>
                                <Select
                                    ref={addValidationRef(11 + index * 2)}
                                    id={id}
                                    label="Pass Type"
                                    isRequired
                                    value={selectValue}
                                    placeholder="Select a pass type ..."
                                    onChange={(e) =>
                                        changeValue({
                                            index,
                                            value: { selectValue: e.target.value },
                                        })
                                    }
                                >
                                    {passOptions.map((item) => (
                                        <SelectOption key={item.value} value={item.value}>
                                            {item.title}
                                        </SelectOption>
                                    ))}
                                </Select>
                            </FlexCol>
                            <FlexCol md={5} lg={4}>
                                <PassImages type={selectValue} />
                            </FlexCol>
                        </FlexRow>
                    )}
                </>
            );
        }
        if (isCochiti(facilityId)) {
            return (
                <>
                    <RadioGroupWithValidation
                        ref={addValidationRef(10 + index * 2)}
                        label="Pass"
                        id={`pass-radios-${id}`}
                        name={`pass-radios-${id}`}
                        value={radioValue}
                        onChange={(e) =>
                            changeValue({ index, value: { radioValue: e.target.value } })
                        }
                        isRequired
                    >
                        <Radio
                            id={`has5thGPass-${id}`}
                            value="has5thGPass"
                            label="I will present a valid U.S. 5th Grade Voucher--valid through August 31, 2021"
                            className="mb-1"
                        />
                        <Radio
                            id={`hasUSACEPass-${id}`}
                            value="hasUSACEPass"
                            label="I will present a valid U.S. Army Corps of Engineers pass"
                            className="mb-1"
                        />
                        <Radio
                            id={`hasUSACVPass-${id}`}
                            value="hasUSACVPass"
                            label="I will present ID proof of my U.S. Armed Service Veteran or Gold Star Dependent status upon arrival"
                            className="mb-1"
                        />
                        <Radio
                            id={`hasPass-${id}`}
                            value="hasPass"
                            label="I will present a valid Interagency Annual/Lifetime Pass"
                            className="mb-1"
                        />
                    </RadioGroupWithValidation>
                    {radioValue === 'hasPass' && (
                        <FlexRow>
                            <FlexCol md={7} lg={8}>
                                <Select
                                    ref={addValidationRef(11 + index * 2)}
                                    id={id}
                                    label="Pass Type"
                                    isRequired
                                    value={selectValue}
                                    placeholder="Select a pass type ..."
                                    onChange={(e) =>
                                        changeValue({
                                            index,
                                            value: { selectValue: e.target.value },
                                        })
                                    }
                                >
                                    {passOptions.map((item) => (
                                        <SelectOption key={item.value} value={item.value}>
                                            {item.title}
                                        </SelectOption>
                                    ))}
                                </Select>
                            </FlexCol>
                            <FlexCol md={5} lg={4}>
                                <PassImages type={selectValue} />
                            </FlexCol>
                        </FlexRow>
                    )}
                </>
            );
        }
        if (isMountEvans(facilityId)) {
            return (
                <>
                    <RadioGroupWithValidation
                        ref={addValidationRef(10 + index * 2)}
                        label="Pass"
                        id={`pass-radios-${id}`}
                        name={`pass-radios-${id}`}
                        value={radioValue}
                        onChange={(e) =>
                            changeValue({ index, value: { radioValue: e.target.value } })
                        }
                        isRequired
                    >
                        <Radio
                            id={`sitePass-${id}`}
                            value="sitePass"
                            label="I will present a valid site pass for the location I am planning to visit"
                            className="mb-1"
                        />
                        <Radio
                            id={`has5thGPass-${id}`}
                            value="has5thGPass"
                            label="I will present a valid U.S. 5th Grade Voucher--valid through August 31, 2021"
                            className="mb-1"
                        />
                        <Radio
                            id={`hasPass-${id}`}
                            value="hasPass"
                            label="I will present a valid Interagency Annual/Lifetime Pass"
                            className="mb-1"
                        />
                    </RadioGroupWithValidation>
                    {radioValue === 'hasPass' && (
                        <FlexRow>
                            <FlexCol md={7} lg={8}>
                                <Select
                                    ref={addValidationRef(11 + index * 2)}
                                    id={id}
                                    label="Pass Type"
                                    isRequired
                                    value={selectValue}
                                    placeholder="Select a pass type ..."
                                    onChange={(e) =>
                                        changeValue({
                                            index,
                                            value: { selectValue: e.target.value },
                                        })
                                    }
                                >
                                    {passOptions.map((item) => (
                                        <SelectOption key={item.value} value={item.value}>
                                            {item.title}
                                        </SelectOption>
                                    ))}
                                </Select>
                            </FlexCol>
                            <FlexCol md={5} lg={4}>
                                <PassImages type={selectValue} />
                            </FlexCol>
                        </FlexRow>
                    )}
                </>
            );
        }
        if (isBrainardLake(facilityId)) {
            return (
                <>
                    <RadioGroupWithValidation
                        ref={addValidationRef(10 + index * 2)}
                        label="Pass"
                        id={`pass-radios-${id}`}
                        name={`pass-radios-${id}`}
                        value={radioValue}
                        onChange={(e) =>
                            changeValue({ index, value: { radioValue: e.target.value } })
                        }
                        isRequired
                    >
                        <Radio
                            id={`has5thGPass-${id}`}
                            value="has5thGPass"
                            label="I will present a valid U.S. 5th Grade Voucher--valid through August 31, 2021"
                            className="mb-1"
                        />
                        <Radio
                            id={`hasUSACVPass-${id}`}
                            value="hasUSACVPass"
                            label="I will present ID proof of my U.S. Armed Service Veteran or Gold Star Dependent status upon arrival"
                            className="mb-1"
                        />
                        <Radio
                            id={`hasPass-${id}`}
                            value="hasPass"
                            label="I will present a valid Interagency Annual/Lifetime Pass"
                            className="mb-1"
                        />
                    </RadioGroupWithValidation>
                    {radioValue === 'hasPass' && (
                        <FlexRow>
                            <FlexCol md={7} lg={8}>
                                <Select
                                    ref={addValidationRef(11 + index * 2)}
                                    id={id}
                                    label="Pass Type"
                                    isRequired
                                    value={selectValue}
                                    placeholder="Select a pass type ..."
                                    onChange={(e) =>
                                        changeValue({
                                            index,
                                            value: { selectValue: e.target.value },
                                        })
                                    }
                                >
                                    {passOptions.map((item) => (
                                        <SelectOption key={item.value} value={item.value}>
                                            {item.title}
                                        </SelectOption>
                                    ))}
                                </Select>
                            </FlexCol>
                            <FlexCol md={5} lg={4}>
                                <PassImages type={selectValue} />
                            </FlexCol>
                        </FlexRow>
                    )}
                </>
            );
        }
        return null;
    };

    if (radioValue === 'init' && selectValue === 'init') {
        return null;
    }

    return (
        <Box padding="md" background="gray-1" border="gray">
            <Stack>
                <Heading headingLevel={4} appearance="small">
                    {quantity > 1
                        ? `Select Pass for Ticket ${index + 1} of ${quantity}`
                        : 'Select Pass'}
                </Heading>
                {getRadioGroup()}
                <div>
                    <Button
                        iconBeforeElement={<Icon iconName="delete" />}
                        appearance="link"
                        onClick={() => deletePassPicker(index)}
                    >
                        Remove Pass
                    </Button>
                </div>
            </Stack>
        </Box>
    );
}

PassPicker.propTypes = {
    value: PropTypes.shape({
        radioValue: PropTypes.string,
        selectValue: PropTypes.string,
    }),
    changeValue: PropTypes.func,
    deletePassPicker: PropTypes.func,
    index: PropTypes.number,
    quantity: PropTypes.number,
    addValidationRef: PropTypes.func,
    facilityId: PropTypes.number,
};

export default function AddPass({ reservation, addValidationRef }) {
    const dispatch = useDispatch();
    // Redux state
    const reduxPasses = useSelector(
        (state) => state.timedEntry.orderDetails?.reservation?.passes
    );

    //Set a default value that is not a valid pass, whenever we found these values
    //we don't render neither the passes section nor the summary price details
    // Local state
    const [passValues, setPassValues] = useState([
        { radioValue: 'init', selectValue: 'init' },
    ]);

    // Derived state
    const { ticket_count_by_guest_type_id: ticketCountById, facility_id: facilityId } =
        reservation;
    const quantity = Object.keys(ticketCountById).reduce((sum, key) => {
        return sum + ticketCountById[key];
    }, 0);

    // On load, get passes from the reservation stored in redux and move
    // them into local state. Primarily for modifications
    useEffect(() => {
        if (!reduxPasses || reduxPasses.length === 0) {
            return;
        }

        const newState = [];

        reduxPasses.forEach((item) => {
            let radioValue = '';
            let selectValue = '';

            switch (item?.type) {
                case 'NOT_YET_PURCHASED':
                    radioValue = 'noPass';
                    break;
                case 'SITE_PASS':
                    radioValue = 'sitePass';
                    selectValue = 'SITE_PASS';
                    break;
                case 'USACE_PASS':
                    radioValue = 'hasUSACEPass';
                    selectValue = 'USACE_PASS';
                    break;
                case 'VETERAN_GOLD_STAR_FAMILY':
                    radioValue = 'hasUSACVPass';
                    selectValue = 'VETERAN_GOLD_STAR_FAMILY';
                    break;
                case '5TH_GRADE_VOUCHER':
                    radioValue = 'has5thGPass';
                    selectValue = '5TH_GRADE_VOUCHER';
                    break;
                default:
                    radioValue = 'hasPass';
                    selectValue = item.type;
                    break;
            }

            newState.push({ radioValue, selectValue });
        });

        setPassValues(newState);
    }, [reduxPasses]);

    function shouldUpdatePriceSummary(passes) {
        if (passes[0]?.radioValue === 'init' && passes[0]?.selectValue === 'init') {
            return false;
        }
        return true;
    }

    useEffect(() => {
        if (shouldUpdatePriceSummary(passValues)) {
            const passes = passValues.map((pass) => ({
                type: pass.selectValue ? pass.selectValue : 'NOT_YET_PURCHASED',
            }));
            dispatch(changePassesDetails(passes));
            dispatch(getDraftPriceSummary(passes));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [passValues]);

    function onPassPickerChange({ index, value }) {
        setPassValues((prev) => {
            if (prev[0]?.radioValue === 'init' && prev[0]?.selectValue === 'init') {
                prev.splice(0, 1);
            }

            const newValues = [...prev];

            newValues[index] = {
                ...prev[index],
                ...value,
            };

            switch (value.radioValue) {
                case 'noPass':
                    newValues[index].selectValue = '';
                    break;
                case 'hasPass':
                    newValues[index].selectValue = '';
                    break;
                case 'sitePass':
                    newValues[index].selectValue = 'SITE_PASS';
                    break;
                case 'hasUSACEPass':
                    newValues[index].selectValue = 'USACE_PASS';
                    break;
                case 'hasUSACVPass':
                    newValues[index].selectValue = 'VETERAN_GOLD_STAR_FAMILY';
                    break;
                case 'has5thGPass':
                    newValues[index].selectValue = '5TH_GRADE_VOUCHER';
                    break;
                default:
                    break;
            }

            return newValues;
        });
    }

    function renderAddPassButton() {
        if (
            passValues[0]?.selectValue === 'init' &&
            passValues[0]?.radioValue === 'init'
        ) {
            return true;
        }
        return passValues.length < quantity;
    }

    function renderButtonLabel() {
        if (
            passValues[0]?.selectValue === 'init' &&
            passValues[0]?.radioValue === 'init'
        ) {
            return true;
        }
        return passValues.length < 1;
    }

    function deletePassPicker(index) {
        setPassValues((prev) => {
            const newValues = [...prev];
            newValues.splice(index, 1);
            return newValues;
        });
    }

    function addPassPicker() {
        setPassValues((prev) => {
            if (prev[0]?.radioValue === 'init' && prev[0]?.selectValue === 'init') {
                prev.splice(0, 1);
            }
            return [...prev, { radioValue: '', selectValue: '' }];
        });
    }

    function renderPassForms() {
        return passValues.map((value, index) => (
            <PassPicker
                addValidationRef={addValidationRef}
                key={`pass-picker-${index}`}
                value={value}
                index={index}
                quantity={quantity}
                changeValue={onPassPickerChange}
                deletePassPicker={deletePassPicker}
                facilityId={facilityId}
            />
        ));
    }

    return (
        <section>
            <Stack>
                <Heading headingLevel={3} appearance="h6">
                    Apply a Pass
                </Heading>
                <Text size="sm">
                    <strong>Note:</strong> Your annual or lifetime pass is accepted for
                    your entrance/standard amenity fee only. A non-refundable reservation
                    fee is required for each ticket. Individual pass holders must present
                    their passes to field agents upon check-in. Depending on the site, you
                    may be able to purchase a pass upon arrival, please be prepared to pay
                    for your pass with a credit card.
                </Text>

                <Stack>{renderPassForms()}</Stack>

                {renderAddPassButton() && (
                    <div>
                        <Button
                            appearance="secondary"
                            iconBeforeElement={<Icon iconName="add-circle" />}
                            onClick={addPassPicker}
                        >
                            {renderButtonLabel() ? 'Add a pass' : 'Add another pass'}
                        </Button>
                    </div>
                )}
            </Stack>
        </section>
    );
}

AddPass.propTypes = {
    reservation: PropTypes.shape({
        ticket_count_by_guest_type_id: PropTypes.object,
        facility_id: PropTypes.number,
    }),
    addValidationRef: PropTypes.func,
};
